import RefundForm from '@/components/forms/Refunds/RefundForm'
import { Typography } from '@/components/ui/typography'
import dayjs from '@/lib/dayjs'
import { CustomerPaymentTerms } from '@/lib/enums/CustomerPaymentTerms'
import { RefundSettlementStatus } from '@/lib/enums/RefundSettlementStatus'
import { TransactionLineType } from '@/lib/enums/TransactionLineType'
import type { RefundRequest } from '@/lib/services/refunds/refunds.types'
import type {
  OrderSourceLink,
  SalesOrder,
  SalesOrderDocumentLine,
  SalesOrderFinancialState,
  SalesOrderMoneyLine,
} from '@/lib/services/sales-orders/sales-orders.types'
import { cn, formatCurrency, parseCurrency } from '@/lib/utils'
import { Fragment } from 'react'

/** The refund request fields these helpers read. */
type RefundRequestStatusFields = Partial<Pick<RefundRequest, 'settlement_status' | 'status'>>

type RefundRequestMoneyFields = RefundRequestStatusFields &
  Partial<Pick<RefundRequest, 'amount' | 'settled_total' | 'failed_total' | 'pending_total'>>

interface RefundRequestPresentation {
  eyebrow: string
  amountText: string
  amountClassName: string
  statusText: string | null
  statusClassName: string
}

/** The order fields the discount row reads: the documents' discount line and the document's discount line identity. */
interface DiscountRowsOrder {
  customer_total_lines?: SalesOrderMoneyLine[]
  document?: {
    line_items: Pick<SalesOrderDocumentLine, 'id' | 'type' | 'memo' | 'discount_summary'>[]
  } | null
}

interface DiscountRow {
  id: number | string
  code?: string
  discountId?: number
  label: string
  amount: string
}

type CondolencesSummary = NonNullable<OrderSourceLink['condolences_summary']>

function SalesOrderRefundAsideContent({
  order,
  onCompleted,
}: {
  order: SalesOrder
  onCompleted?: () => void
}) {
  const refundPaymentState = order?.payment_snapshot
    ? {
        ...order.payment_snapshot,
        id: order?.cart_id ?? null,
        refund_preview: order.refund_preview ?? null,
      }
    : null

  return <RefundForm order={order} cart={refundPaymentState} onCompleted={onCompleted} />
}

function formatSalesOrderPaymentTerms(
  order: Partial<Pick<SalesOrder, 'payment_terms_code' | 'payment_terms_days'>> | null | undefined
): string {
  if (!order?.payment_terms_code) {
    return 'Due on receipt'
  }

  if (order.payment_terms_code === CustomerPaymentTerms.Custom.Value) {
    return order.payment_terms_days ? `${order.payment_terms_days} days` : 'Custom terms'
  }

  return (
    Object.values(CustomerPaymentTerms).find((term) => term.Value === order.payment_terms_code)
      ?.Name ?? String(order.payment_terms_code).replaceAll('_', ' ')
  )
}

function formatRefundRequestTimestamp(timestamp: string | null | undefined): string | null {
  return timestamp ? dayjs(timestamp).format('MMM D, YYYY h:mm A') : null
}

function normalizeRefundRequestSettlementStatus(
  refundRequest: RefundRequestStatusFields | null | undefined
): string | null {
  const settlementStatus = String(refundRequest?.settlement_status ?? '')
    .trim()
    .toLowerCase()
  if (settlementStatus) {
    return settlementStatus
  }

  const requestStatus = String(refundRequest?.status ?? '')
    .trim()
    .toLowerCase()
  if (requestStatus === 'processed') {
    return RefundSettlementStatus.Settled.Value
  }

  if (requestStatus === 'authorized') {
    return RefundSettlementStatus.PendingSettlement.Value
  }

  return null
}

export function isRefundRequestSettled(
  refundRequest: RefundRequestStatusFields | null | undefined
): boolean {
  const settlementStatus = normalizeRefundRequestSettlementStatus(refundRequest)
  return settlementStatus === null || settlementStatus === RefundSettlementStatus.Settled.Value
}

function summarizeRefundRequestStatuses(refundRequests: RefundRequestStatusFields[] = []) {
  return refundRequests.reduce(
    (summary, refundRequest) => {
      const settlementStatus = normalizeRefundRequestSettlementStatus(refundRequest)

      if (settlementStatus === RefundSettlementStatus.Failed.Value) {
        summary.failed += 1
      }

      if (settlementStatus === RefundSettlementStatus.PartiallySettled.Value) {
        summary.partial += 1
      }

      if (settlementStatus === RefundSettlementStatus.PendingSettlement.Value) {
        summary.pending += 1
      }

      return summary
    },
    { failed: 0, partial: 0, pending: 0 }
  )
}

// Refunded is money that went back (`financial_state.refunded_total`); pending refunds show on their own rows (money rule 3).
function formatRefundsCardTitle(
  financialState: Pick<SalesOrderFinancialState, 'refunded_total'> | null | undefined
): string {
  const refundedTotal = financialState?.refunded_total ?? null

  return parseCurrency(refundedTotal ?? 0) > 0
    ? `Refunded ${formatCurrency(refundedTotal)}`
    : 'Refunds'
}

function formatRefundsCardSubtitle(refundRequests: RefundRequestStatusFields[] = []): string {
  const { failed, partial, pending } = summarizeRefundRequestStatuses(refundRequests)
  const parts = [
    refundRequests.length === 1 ? '1 refund record' : `${refundRequests.length} refund records`,
  ]

  if (failed > 0) {
    parts.push(failed === 1 ? '1 failed' : `${failed} failed`)
  }

  if (partial > 0) {
    parts.push(partial === 1 ? '1 partial' : `${partial} partial`)
  }

  if (pending > 0) {
    parts.push(pending === 1 ? '1 pending' : `${pending} pending`)
  }

  return parts.join(' · ')
}

function formatDeliveryFeeSource(source: string): string {
  switch (source) {
    case 'fallback_city':
      return 'City fallback'
    case 'fallback_postal_code':
      return 'ZIP fallback'
    case 'city':
      return 'City fee'
    case 'postal_code':
      return 'ZIP fee'
    case 'distance_range':
      return 'Driving distance fee'
    case 'special_price_zone':
      return 'Special price'
    case 'condolences':
      return 'Condolences'
    default:
      return source ? source.replaceAll('_', ' ') : 'Fallback fee'
  }
}

function formatDeliveryFeeFallbackReason(reason: string): string {
  switch (reason) {
    case 'route_unavailable':
      return 'Google could not calculate the driving route, so checkout used the base service-area fee.'
    case 'unverified_address':
      return 'The address was not verified, so checkout used the base service-area fee.'
    case 'missing_route_waypoint':
      return 'The address did not include enough route information, so checkout used the base service-area fee.'
    case 'missing_max_distance':
      return 'Driving-distance pricing was incomplete, so checkout used the base service-area fee.'
    case 'missing_distance_range':
      return 'No driving-distance range matched the calculated route, so checkout used the base service-area fee.'
    default:
      return 'Checkout used the base service-area fee instead of an advanced delivery rule.'
  }
}

function buildRefundRequestPresentation(
  refundRequest: RefundRequestMoneyFields | null | undefined
): RefundRequestPresentation {
  const settlementStatus = normalizeRefundRequestSettlementStatus(refundRequest)
  const requestedTotal = refundRequest?.amount ?? 0
  const settledTotal = refundRequest?.settled_total ?? 0
  const failedTotal = refundRequest?.failed_total ?? 0
  const pendingTotal = parseCurrency(refundRequest?.pending_total)

  if (settlementStatus === RefundSettlementStatus.Failed.Value) {
    return {
      eyebrow: 'Refund failed',
      amountText: `Attempted ${formatCurrency(requestedTotal)}`,
      amountClassName: 'text-destructive',
      statusText: 'No money was sent back to the customer.',
      statusClassName: 'text-sm font-medium leading-6 text-destructive',
    }
  }

  if (settlementStatus === RefundSettlementStatus.PartiallySettled.Value) {
    return {
      eyebrow: 'Refund partially completed',
      amountText: `Settled ${formatCurrency(settledTotal)} · Failed ${formatCurrency(failedTotal)}`,
      amountClassName: 'text-status-warning-foreground dark:text-status-warning-foreground',
      statusText: 'Part of this refund failed. Review the outcome before trying again.',
      statusClassName:
        'text-sm font-medium leading-6 text-status-warning-foreground dark:text-status-warning-foreground',
    }
  }

  if (settlementStatus === RefundSettlementStatus.PendingSettlement.Value) {
    return {
      eyebrow: 'Refund pending',
      amountText: `Requested ${formatCurrency(requestedTotal)}`,
      amountClassName: 'text-foreground',
      statusText:
        pendingTotal > 0
          ? `${formatCurrency(pendingTotal)} is still waiting for confirmation.`
          : 'This refund is still waiting for confirmation.',
      statusClassName: 'text-sm leading-6 text-muted-foreground',
    }
  }

  return {
    eyebrow: 'Refund',
    amountText: `Refunded ${formatCurrency(settledTotal)}`,
    amountClassName: 'text-foreground',
    statusText: null,
    statusClassName: '',
  }
}

// The order's discount as its documents show it (`customer_total_lines`), named by the document's discount line.
function getDiscountRows(order: DiscountRowsOrder | null | undefined): DiscountRow[] {
  const discount = (order?.customer_total_lines ?? []).find((line) => line.key === 'discount')
  if (!discount) {
    return []
  }
  const discountLines = (order?.document?.line_items ?? []).filter(
    (item) => item.type === TransactionLineType.Discount.Value
  )
  const item = discountLines.length === 1 ? discountLines[0] : null
  const discountSummary = item?.discount_summary ?? null
  const allocations = discountSummary?.allocations ?? []
  const firstAllocation = allocations.length === 1 ? allocations[0] : undefined
  const code = discountSummary?.code || firstAllocation?.code
  const discountId = discountSummary?.discount_id || firstAllocation?.discount_id

  return [
    {
      id: item?.id ?? discount.key,
      ...(code ? { code } : {}),
      ...(discountId ? { discountId } : {}),
      label: discountSummary?.name || firstAllocation?.name || item?.memo || discount.label,
      amount: discount.amount,
    },
  ]
}

function CondolencesProductsFinancialSummary({
  summary,
  useAlternateSurface = false,
}: {
  summary: CondolencesSummary
  useAlternateSurface?: boolean
}) {
  return (
    <div
      data-testid="condolences-products-financial-summary"
      data-surface-tone={useAlternateSurface ? 'alternate' : 'default'}
      className={cn(
        '@container/summary border-t border-border/60 px-4 py-4 sm:px-5',
        useAlternateSurface && 'bg-elevated'
      )}
    >
      <div className="grid gap-4 @[48rem]/summary:grid-cols-2">
        <CondolencesFinancialColumn
          title="Customer paid"
          rows={[
            ['Products', summary.customer_products_total],
            ['Delivery', summary.customer_delivery_total],
          ]}
          total={summary.customer_total}
        />
        <CondolencesFinancialColumn
          title="Florist payout"
          rows={[
            ['Products', summary.vendor_products_total],
            ['Delivery', summary.vendor_delivery_total],
          ]}
          total={summary.vendor_total}
        />
      </div>
    </div>
  )
}

function CondolencesFinancialColumn({
  title,
  rows,
  total,
}: {
  title: string
  rows: [label: string, value: string | null][]
  total: string | null
}) {
  return (
    <div className="min-w-0 rounded-xl border border-border/60 bg-background/70 px-3 py-3">
      <Typography variant="eyebrow">{title}</Typography>
      <div className="mt-2 grid grid-cols-[1fr_auto] gap-x-5 gap-y-1 text-sm">
        {rows
          .filter(([, value]) => value !== null)
          .map(([label, value]) => (
            <Fragment key={label}>
              <span className="text-muted-foreground">{label}</span>
              <span className="text-right tabular-nums">{formatCurrency(value)}</span>
            </Fragment>
          ))}
      </div>
      {total !== null ? (
        <div className="mt-2 border-t border-border/60 pt-2">
          <div className="grid grid-cols-[1fr_auto] gap-x-5 text-sm">
            <span className="font-medium text-foreground">Total</span>
            <span className="text-right font-semibold tabular-nums text-foreground">
              {formatCurrency(total)}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export {
  CondolencesProductsFinancialSummary,
  SalesOrderRefundAsideContent,
  buildRefundRequestPresentation,
  formatDeliveryFeeFallbackReason,
  formatDeliveryFeeSource,
  formatRefundRequestTimestamp,
  formatRefundsCardSubtitle,
  formatRefundsCardTitle,
  formatSalesOrderPaymentTerms,
  getDiscountRows,
}
